<template>
  <div class="embedded-low-code-page-render">
    <div v-if="loading" class="embedded-low-code-page-render__loading">
      <el-skeleton :rows="5" animated />
    </div>
    <div v-else-if="errorMessage" class="embedded-low-code-page-render__state">
      <p>{{ errorMessage }}</p>
      <el-button type="primary" @click="loadPage">{{ ct('重试') }}</el-button>
    </div>
    <PageRender
      v-else-if="activeDetailData.currentPageConfig"
      :active-detail-data="activeDetailData"
      :is-mobile="isMobile"
      :is-page="false"
    />
    <div v-else class="embedded-low-code-page-render__state">
      {{ emptyText || ct('页面暂无内容') }}
    </div>
  </div>
</template>

<script setup>
const ct = useCommonUiTranslation()

import { inject, onBeforeUnmount, provide, reactive, ref, watch } from "vue";
import PageRender from "@/components/render/PageRender.vue";
import { getActiveDetail } from "@/api";
import { makeAllArcListMap } from "@/utils/asyncData.js";

const props = defineProps({
  pageId: {
    type: [String, Number],
    default: "",
  },
  areaData: {
    type: String,
    default: "",
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  parentContext: {
    type: Object,
    default: null,
  },
  popupController: {
    type: Object,
    default: null,
  },
  emptyText: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["loaded", "error"]);
const activeDetailData = reactive({
  currentPageConfig: null,
  pages: [],
  allArcListMap: {},
});
const loading = ref(false);
const errorMessage = ref("");
let requestVersion = 0;
let embeddedStyleElement = null;
let embeddedScriptElement = null;

const injectedGetConfigJson = inject("getConfigJson", () => ({}));
const parentGetCommonConfigJson = inject("getCommonConfigJson", () => ({}));

function getParentContext() {
  if (props.parentContext) return props.parentContext;
  return typeof injectedGetConfigJson === "function"
    ? injectedGetConfigJson()
    : {};
}

function getEmbeddedConfigJson() {
  const parentContext = getParentContext() || {};
  const currentPageConfig = activeDetailData.currentPageConfig || {};
  const parentBindData = parentContext.bindData || {};
  const currentBindData = currentPageConfig.bindData || {};
  return {
    ...currentPageConfig,
    langJson: {
      ...(parentContext.langJson || {}),
      ...(currentPageConfig.langJson || {}),
    },
    bindData: {
      ...parentBindData,
      ...currentBindData,
      common: {
        ...(parentBindData.common || {}),
        ...(currentBindData.common || {}),
        isH5: props.isMobile,
        isMobile: props.isMobile,
      },
    },
  };
}

provide("getConfigJson", getEmbeddedConfigJson);
provide("getCommonConfigJson", parentGetCommonConfigJson);
provide("lowCodePopupController", props.popupController || { close: () => {} });

function clearEmbeddedStyle() {
  if (embeddedStyleElement?.parentNode) {
    embeddedStyleElement.parentNode.removeChild(embeddedStyleElement);
  }
  embeddedStyleElement = null;
}

function clearEmbeddedScript() {
  if (embeddedScriptElement?.parentNode) {
    embeddedScriptElement.parentNode.removeChild(embeddedScriptElement);
  }
  embeddedScriptElement = null;
}

function applyEmbeddedStyle() {
  clearEmbeddedStyle();
  if (!process.client) return;
  const cssText = activeDetailData.currentPageConfig?.info?.cssText;
  if (!cssText) return;
  embeddedStyleElement = document.createElement("style");
  embeddedStyleElement.dataset.lowCodePopupPage = String(props.pageId || "inline");
  embeddedStyleElement.textContent = cssText;
  document.head.appendChild(embeddedStyleElement);
}

function applyEmbeddedScript() {
  clearEmbeddedScript();
  if (!process.client) return;
  const javascriptText = activeDetailData.currentPageConfig?.info?.javascriptText;
  if (!javascriptText) return;
  embeddedScriptElement = document.createElement("script");
  embeddedScriptElement.dataset.lowCodePopupPage = String(props.pageId || "inline");
  embeddedScriptElement.textContent = javascriptText;
  document.body.appendChild(embeddedScriptElement);
}

function resetActiveDetailData() {
  activeDetailData.currentPageConfig = null;
  activeDetailData.pages = [];
  activeDetailData.allArcListMap = {};
  clearEmbeddedStyle();
  clearEmbeddedScript();
}

async function buildFromAreaData(areaData) {
  const parsedPages = JSON.parse(areaData);
  const { $customFetch } = useNuxtApp();
  return makeAllArcListMap(
    $customFetch,
    {},
    { dataList: [{ areaData }] },
    {},
    parsedPages
  );
}

async function buildFromPageId(pageId) {
  const { $customFetch } = useNuxtApp();
  const response = await getActiveDetail(
    {
      id: pageId,
      status: 1,
      pageNo: 1,
      pageSize: 1,
      getCache: 1,
    },
    $customFetch,
    {}
  );
  const responseData = response?.data;
  if (!responseData?.dataList?.[0]?.areaData) {
    throw new Error(ct('低代码页面不存在、未发布或暂无内容'));
  }
  return makeAllArcListMap(
    $customFetch,
    {},
    responseData,
    {},
    []
  );
}

async function loadPage() {
  const currentRequestVersion = ++requestVersion;
  errorMessage.value = "";
  resetActiveDetailData();
  if (!props.pageId && !props.areaData) return;
  loading.value = true;
  try {
    const result = props.areaData
      ? await buildFromAreaData(props.areaData)
      : await buildFromPageId(props.pageId);
    if (currentRequestVersion !== requestVersion) return;
    activeDetailData.currentPageConfig = result?.pages?.[0] || null;
    activeDetailData.pages = result?.pages || [];
    activeDetailData.allArcListMap = result?.allArcListMap || {};
    if (!activeDetailData.currentPageConfig) {
      throw new Error(ct('低代码页面暂无可渲染内容'));
    }
    applyEmbeddedStyle();
    applyEmbeddedScript();
    emit("loaded", activeDetailData);
  } catch (error) {
    if (currentRequestVersion !== requestVersion) return;
    resetActiveDetailData();
    errorMessage.value = error?.message || ct('低代码页面加载失败');
    emit("error", error);
  } finally {
    if (currentRequestVersion === requestVersion) loading.value = false;
  }
}

watch(
  () => [props.pageId, props.areaData],
  () => loadPage(),
  { immediate: true }
);

onBeforeUnmount(() => {
  requestVersion += 1;
  clearEmbeddedStyle();
  clearEmbeddedScript();
});

defineExpose({ loadPage });
</script>

<style scoped>
.embedded-low-code-page-render {
  width: 100%;
  min-width: 0;
  min-height: 100%;
}

.embedded-low-code-page-render__loading {
  padding: 20px;
}

.embedded-low-code-page-render__state {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  color: var(--el-text-color-secondary);
  text-align: center;
}

.embedded-low-code-page-render__state p {
  max-width: 100%;
  margin: 0;
  overflow-wrap: anywhere;
}
</style>
